import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import moment from "moment";
import {
  PageHeader,
  Card,
  Row,
  Col,
  Table,
  Tag,
  Button,
  Descriptions,
  Modal,
  Form,
  InputNumber,
  Select,
  Input,
  DatePicker,
  Checkbox,
  Divider,
  Popconfirm,
  Spin,
  message,
} from "antd";
import {
  SendOutlined,
  DollarOutlined,
  EditOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { handleApiSuccess, handleApiError, MyBreadcrumb, confirmDelete } from "utils";
import {
  billingInvoiceByIdService,
  billingInvoiceStatusService,
  billingPaymentByInvoiceService,
  billingPaymentCreateService,
  billingPaymentDeleteService,
} from "services/billing";
import { statusMeta, money, PAYMENT_METHODS } from "../constants";
import { handleInvoiceSaveResponse } from "./_emailFeedback";

const { Option } = Select;

export default function InvoiceView() {
  const { id } = useParams();
  const history = useHistory();
  const [form] = Form.useForm();
  const [invoice, setInvoice] = useState(null);
  const [payments, setPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [payModal, setPayModal] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadAll = () => {
    setIsLoading(true);
    Promise.all([
      billingInvoiceByIdService(id),
      billingPaymentByInvoiceService(id),
    ])
      .then(([invRes, payRes]) => {
        setIsLoading(false);
        setInvoice(invRes.data.item);
        setPayments(payRes.data.items || []);
      })
      .catch((error) => {
        setIsLoading(false);
        handleApiError(error);
      });
  };

  const changeStatus = (status, sendEmail) => {
    if (status === "SENT" && sendEmail !== false) {
      const email = (invoice && invoice.bill_to_email || "").trim();
      if (!email) {
        message.error("Bill To email is empty. Edit the invoice and add an email first.");
        return;
      }
    }
    billingInvoiceStatusService(id, status, sendEmail)
      .then((response) => {
        handleInvoiceSaveResponse(response);
        loadAll();
      })
      .catch((error) => handleApiError(error));
  };

  const openPayModal = () => {
    form.setFieldsValue({
      amount: invoice ? invoice.amount_due : 0,
      payment_date: moment(),
      payment_method: "BANK_TRANSFER",
      send_email: true,
    });
    setPayModal(true);
  };

  const recordPayment = () => {
    form.validateFields().then((values) => {
      setSaving(true);
      const gatewayMap = {
        STRIPE: "STRIPE",
        PAYPAL: "PAYPAL",
        CRYPTO_BTC: "CRYPTO",
        CRYPTO_ETH: "CRYPTO",
      };
      billingPaymentCreateService({
        invoice: id,
        amount: Number(values.amount),
        payment_date: values.payment_date
          ? values.payment_date.format("YYYY-MM-DD")
          : null,
        payment_method: values.payment_method,
        gateway: gatewayMap[values.payment_method] || "MANUAL",
        payment_id: values.payment_id,
        internal_note: values.internal_note,
        send_email: values.send_email,
      })
        .then((response) => {
          setSaving(false);
          setPayModal(false);
          handleApiSuccess(response);
          loadAll();
        })
        .catch((error) => {
          setSaving(false);
          handleApiError(error);
        });
    });
  };

  const deletePayment = (paymentId) => {
    confirmDelete().then(() => {
      billingPaymentDeleteService(paymentId)
        .then((response) => {
          handleApiSuccess(response);
          loadAll();
        })
        .catch((error) => handleApiError(error));
    });
  };

  if (!invoice) {
    return (
      <Spin spinning={isLoading}>
        <div style={{ minHeight: 200 }} />
      </Spin>
    );
  }

  const m = statusMeta(invoice.status);

  const itemColumns = [
    { title: "Item", dataIndex: "product_name", key: "product_name",
      render: (t, r) => (
        <div>
          <div>{t}</div>
          {r.description ? <div style={{ color: "#888", fontSize: 12 }}>{r.description}</div> : null}
        </div>
      ),
    },
    { title: "Qty/Hours", dataIndex: "quantity", key: "quantity",
      render: (t, r) => `${t || 0}${r.unit ? " " + r.unit : ""}` },
    { title: "Rate", dataIndex: "rate", key: "rate", align: "right", render: (t) => money(t, invoice.currency) },
    { title: "Disc %", dataIndex: "discount_percentage", key: "disc", align: "right", render: (t) => (t ? t + "%" : "-") },
    { title: "Tax", dataIndex: "tax_percentage", key: "tax", align: "right",
      render: (t, r) => (r.is_taxable === false ? <Tag>No Tax</Tag> : (t ? t + "%" : "-")) },
    { title: "Total", dataIndex: "amount_total", key: "amount_total", align: "right", render: (t) => money(t, invoice.currency) },
  ];

  const paymentColumns = [
    { title: "Date", dataIndex: "payment_date", key: "payment_date" },
    { title: "Amount", dataIndex: "amount", key: "amount", render: (t) => money(t, invoice.currency) },
    { title: "Method", dataIndex: "payment_method", key: "payment_method",
      render: (t) => (PAYMENT_METHODS.find((p) => p.value === t) || {}).label || t },
    { title: "Transaction ID", dataIndex: "payment_id", key: "payment_id" },
    { title: "Note", dataIndex: "internal_note", key: "internal_note" },
    { title: "Emailed", dataIndex: "email_sent", key: "email_sent",
      render: (t) => (t ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>) },
    { title: "", key: "action",
      render: (t, r) => (
        <Button danger size="small" onClick={() => deletePayment(r._id)}>Delete</Button>
      ),
    },
  ];

  return (
    <div>
      <MyBreadcrumb
        items={[
          { label: "Home", path: "/" },
          { label: "Billing", path: "" },
          { label: "Invoices", path: "/billing/invoices" },
          { label: invoice.invoice_number, path: "" },
        ]}
      />
      <PageHeader
        ghost={false}
        title={invoice.invoice_number}
        tags={<Tag color={m.color}>{m.label}</Tag>}
        extra={[
          <Button key="edit" icon={<EditOutlined />} onClick={() => history.push("/billing/invoices/update/" + id)}>
            Edit
          </Button>,
          <Button key="send" icon={<SendOutlined />} onClick={() => changeStatus("SENT", true)}>
            Send
          </Button>,
          <Button key="pay" type="primary" icon={<DollarOutlined />} onClick={openPayModal} disabled={invoice.amount_due <= 0}>
            Record Payment
          </Button>,
          <Popconfirm key="cancel" title="Cancel this invoice?" onConfirm={() => changeStatus("CANCELLED", false)}>
            <Button danger icon={<StopOutlined />}>Cancel</Button>
          </Popconfirm>,
        ]}
      />

      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col xs={24} lg={16}>
          <Card size="small" style={{ marginBottom: 16 }}>
            <Descriptions column={2} size="small">
              <Descriptions.Item label="Customer">
                {(invoice.company && invoice.company.company_name) || invoice.bill_to_name}
              </Descriptions.Item>
              <Descriptions.Item label="Email">{invoice.bill_to_email}</Descriptions.Item>
              <Descriptions.Item label="Address" span={2}>{invoice.bill_to_address}</Descriptions.Item>
              <Descriptions.Item label="Invoice Date">{invoice.invoice_date}</Descriptions.Item>
              <Descriptions.Item label="Due Date">{invoice.invoice_due_date}</Descriptions.Item>
              <Descriptions.Item label="Service Period">{invoice.service_period_label}</Descriptions.Item>
              <Descriptions.Item label="Sales Agent">{invoice.sales_agent_name}</Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="Items" size="small" style={{ marginBottom: 16 }}>
            <Table
              dataSource={invoice.items}
              columns={itemColumns}
              pagination={false}
              rowKey={(r, i) => r._id || i}
              size="small"
            />
          </Card>

          <Card title="Payments" size="small">
            <Table
              dataSource={payments}
              columns={paymentColumns}
              pagination={false}
              rowKey="_id"
              size="small"
              locale={{ emptyText: "No payments recorded" }}
            />
          </Card>

          {(invoice.client_note || invoice.terms_conditions) && (
            <Card size="small" style={{ marginTop: 16 }}>
              {invoice.client_note && (
                <div style={{ marginBottom: 12 }}>
                  <strong>Client Note</strong>
                  <p>{invoice.client_note}</p>
                </div>
              )}
              {invoice.terms_conditions && (
                <div style={{ color: "#888" }}>
                  <strong>Terms &amp; Conditions</strong>
                  <p>{invoice.terms_conditions}</p>
                </div>
              )}
            </Card>
          )}
        </Col>

        <Col xs={24} lg={8}>
          <Card title="Summary" size="small">
            <Row justify="space-between"><span>Subtotal</span><span>{money(invoice.subtotal, invoice.currency)}</span></Row>
            <Row justify="space-between"><span>Discount</span><span>-{money(invoice.discount_amount, invoice.currency)}</span></Row>
            <Row justify="space-between"><span>Tax</span><span>{money(invoice.tax_total, invoice.currency)}</span></Row>
            <Row justify="space-between"><span>Adjustment</span><span>{money(invoice.adjustment, invoice.currency)}</span></Row>
            <Divider style={{ margin: "8px 0" }} />
            <Row justify="space-between"><strong>Grand Total</strong><strong>{money(invoice.grand_total, invoice.currency)}</strong></Row>
            <Row justify="space-between"><span>Paid</span><span>{money(invoice.amount_paid, invoice.currency)}</span></Row>
            <Row justify="space-between"><strong>Balance Due</strong><strong style={{ color: invoice.amount_due > 0 ? "#cf1322" : "#3f8600" }}>{money(invoice.amount_due, invoice.currency)}</strong></Row>
          </Card>
          {invoice.admin_note && (
            <Card title="Admin Note (private)" size="small" style={{ marginTop: 16 }}>
              {invoice.admin_note}
            </Card>
          )}
        </Col>
      </Row>

      <Modal
        title="Record a Payment"
        visible={payModal}
        onOk={recordPayment}
        confirmLoading={saving}
        onCancel={() => setPayModal(false)}
        okText="Record Payment"
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Amount" name="amount" rules={[{ required: true, message: "Enter amount" }]}>
            <InputNumber min={0.01} max={invoice.amount_due} step={0.01} prefix="$" style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label="Payment Date" name="payment_date">
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label="Payment Method" name="payment_method">
            <Select>
              {PAYMENT_METHODS.map((p) => (
                <Option key={p.value} value={p.value}>{p.label}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Payment ID / Transaction Reference" name="payment_id">
            <Input />
          </Form.Item>
          <Form.Item label="Internal Note" name="internal_note">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="send_email" valuePropName="checked">
            <Checkbox>Send payment recorded email to customer</Checkbox>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
